import { createContext, useContext } from "react";

/**
 * Represents a registered hotkey entry in the shell-level registry.
 */
export interface ShellHotkeyEntry {
  /** The unique identifier for this registration. */
  id: string;
  /** The hotkey combination (e.g., "Ctrl+K"). */
  combo: string;
  /** The scope this hotkey is registered under. */
  scope: string;
  /** The module that registered this hotkey. */
  moduleId: string;
  /** Display title for the hotkey. */
  title?: string;
  /** Description of what the hotkey does. */
  description?: string;
  /** Category for grouping in UI. */
  category?: string;
  /** Priority for conflict resolution (higher = wins). */
  priority: number;
  /** Timestamp when registered. */
  registeredAt: number;
}

/**
 * Statistics about hotkey registrations.
 */
export interface HotkeyRegistryStats {
  /** Total number of registered hotkeys. */
  totalCount: number;
  /** Count by module. */
  byModule: Record<string, number>;
  /** Count by scope. */
  byScope: Record<string, number>;
  /** List of potential conflicts (same combo in same scope). */
  conflicts: Array<{ combo: string; scope: string; entries: ShellHotkeyEntry[] }>;
}

/**
 * The shell-level hotkey registry manager interface.
 * Provides cross-module visibility into hotkey registrations.
 */
export interface HotkeyRegistryManager {
  /**
   * Register a hotkey entry in the shell registry.
   * @param entry The hotkey entry to register.
   * @returns A function to unregister this entry.
   */
  register(entry: Omit<ShellHotkeyEntry, "id" | "registeredAt">): () => void;

  /**
   * Get all registered hotkeys.
   * @returns Array of all registered hotkey entries.
   */
  getAll(): ShellHotkeyEntry[];

  /**
   * Get hotkeys registered by a specific module.
   * @param moduleId The module ID to filter by.
   * @returns Array of hotkey entries for the module.
   */
  getByModule(moduleId: string): ShellHotkeyEntry[];

  /**
   * Get hotkeys registered under a specific scope.
   * @param scope The scope to filter by.
   * @returns Array of hotkey entries for the scope.
   */
  getByScope(scope: string): ShellHotkeyEntry[];

  /**
   * Get hotkeys matching a specific combo.
   * @param combo The hotkey combination to search for.
   * @returns Array of hotkey entries with this combo.
   */
  getByCombo(combo: string): ShellHotkeyEntry[];

  /**
   * Get statistics about the registry.
   * @returns Registry statistics including counts and conflicts.
   */
  getStats(): HotkeyRegistryStats;

  /**
   * Check if a combo is already registered in a scope.
   * @param combo The hotkey combination.
   * @param scope The scope to check.
   * @returns True if the combo is already registered in this scope.
   */
  isRegistered(combo: string, scope: string): boolean;

  /**
   * Unregister all hotkeys for a specific module.
   * Useful when a module is being unloaded.
   * @param moduleId The module ID to clear.
   * @returns Number of entries removed.
   */
  clearModule(moduleId: string): number;

  /**
   * Subscribe to registry changes.
   * @param listener Callback fired when registry changes.
   * @returns Unsubscribe function.
   */
  subscribe(listener: (entries: ShellHotkeyEntry[]) => void): () => void;
}

/**
 * Create a new hotkey registry manager instance.
 * This should be created once at the shell level and shared via context.
 */
export function createHotkeyRegistryManager(): HotkeyRegistryManager {
  const entries: Map<string, ShellHotkeyEntry> = new Map();
  const listeners: Set<(entries: ShellHotkeyEntry[]) => void> = new Set();
  let nextId = 1;

  function notifyListeners(): void {
    const allEntries = Array.from(entries.values());
    listeners.forEach((listener) => listener(allEntries));
  }

  function generateId(): string {
    return `hotkey-${nextId++}-${Date.now()}`;
  }

  return {
    register(entry) {
      const id = generateId();
      const fullEntry: ShellHotkeyEntry = {
        ...entry,
        id,
        registeredAt: Date.now(),
      };
      entries.set(id, fullEntry);
      notifyListeners();

      // Return unregister function
      return () => {
        entries.delete(id);
        notifyListeners();
      };
    },

    getAll() {
      return Array.from(entries.values());
    },

    getByModule(moduleId) {
      return Array.from(entries.values()).filter((e) => e.moduleId === moduleId);
    },

    getByScope(scope) {
      return Array.from(entries.values()).filter((e) => e.scope === scope);
    },

    getByCombo(combo) {
      const normalizedCombo = combo.toLowerCase();
      return Array.from(entries.values()).filter((e) => e.combo.toLowerCase() === normalizedCombo);
    },

    getStats() {
      const allEntries = Array.from(entries.values());
      const byModule: Record<string, number> = {};
      const byScope: Record<string, number> = {};
      const comboScopeMap: Map<string, ShellHotkeyEntry[]> = new Map();

      for (const entry of allEntries) {
        // Count by module
        byModule[entry.moduleId] = (byModule[entry.moduleId] || 0) + 1;

        // Count by scope
        byScope[entry.scope] = (byScope[entry.scope] || 0) + 1;

        // Track potential conflicts
        const key = `${entry.combo.toLowerCase()}:${entry.scope}`;
        const existing = comboScopeMap.get(key) || [];
        existing.push(entry);
        comboScopeMap.set(key, existing);
      }

      // Find conflicts (multiple entries with same combo in same scope)
      const conflicts: Array<{ combo: string; scope: string; entries: ShellHotkeyEntry[] }> = [];
      for (const [key, entryList] of comboScopeMap) {
        if (entryList.length > 1) {
          const [combo, scope] = key.split(":");
          conflicts.push({ combo, scope, entries: entryList });
        }
      }

      return {
        totalCount: allEntries.length,
        byModule,
        byScope,
        conflicts,
      };
    },

    isRegistered(combo, scope) {
      const normalizedCombo = combo.toLowerCase();
      return Array.from(entries.values()).some(
        (e) => e.combo.toLowerCase() === normalizedCombo && e.scope === scope,
      );
    },

    clearModule(moduleId) {
      let count = 0;
      for (const [id, entry] of entries) {
        if (entry.moduleId === moduleId) {
          entries.delete(id);
          count++;
        }
      }
      if (count > 0) {
        notifyListeners();
      }
      return count;
    },

    subscribe(listener) {
      listeners.add(listener);
      // Immediately call with current state
      listener(Array.from(entries.values()));

      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Context for the shell-level hotkey registry.
 */
export const HotkeyRegistryContext = createContext<HotkeyRegistryManager | null>(null);

/**
 * Hook to access the shell-level hotkey registry.
 * @returns The hotkey registry manager.
 * @throws If used outside of a HotkeyRegistryContext provider.
 */
export function useShellHotkeyRegistry(): HotkeyRegistryManager {
  const registry = useContext(HotkeyRegistryContext);
  if (!registry) {
    throw new Error(
      "useShellHotkeyRegistry must be used within a HotkeyRegistryContext provider. " +
        "Make sure the shell provides the HotkeyRegistryContext.",
    );
  }
  return registry;
}

/**
 * Hook to get all hotkeys from the shell registry.
 * @returns Array of all registered hotkey entries.
 */
export function useShellHotkeys(): ShellHotkeyEntry[] {
  const registry = useShellHotkeyRegistry();
  return registry.getAll();
}

/**
 * Hook to get hotkey registry statistics.
 * @returns Registry statistics.
 */
export function useShellHotkeyStats(): HotkeyRegistryStats {
  const registry = useShellHotkeyRegistry();
  return registry.getStats();
}
